// frontend/src/components/ETADisplay/LiveTripView.js
// Component for displaying live/active trip ETA

import { useState } from 'react';
import { Clock, MapPin, UserPlus, Briefcase, ArrowDownToLine, ArrowRightFromLine, RotateCcw, CheckCircle, SkipForward, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { formatTimeLocal, formatDistance, formatDuration } from '../../utils/etaFormatters';
import { hasBoardingInfo } from '../../utils/etaWaypointHelpers';
import { useDateTimePreferences } from '../../hooks/useDateTimePreferences';
import ConfirmDialogShell from '../dialogs/ConfirmDialogShell';
import {
    ETA_ARRIVING_THRESHOLD_MINUTES,
    getEffectiveWaypointState,
    getEtaStopDisplayState,
    isFallbackEstimate
} from '../../utils/etaStopDisplayState';

export { getEffectiveWaypointState, isFallbackEstimate };

const finiteNumberOrNull = (value) => {
    if (value === null || typeof value === 'undefined' || value === '') return null;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};

/**
 * ManualOverrideButtons Component
 * Displays manual arrival/departure/correction buttons based on waypoint state
 *
 * Button visibility logic:
 * - pending/approaching: Show both "Arrive" and "Depart" buttons
 * - dwelling/departing: Show only "Depart" button (already arrived)
 * - passed: Show "Correct" button (to reset waypoint to pending state)
 *
 * All actions require confirmation dialog to prevent accidental clicks
 */
const ManualOverrideButtons = ({
    stop,
    waypointState,
    onManualArrival,
    onManualDeparture,
    onManualSkip,
    onManualCorrection,
    getTranslatedWaypointName,
    isFirstWaypoint,
    isLastWaypoint,
    preTripCommunication = false,
    confirmCorrectionInline = false,
    t,
    cn
}) => {
    const [isArriveLoading, setIsArriveLoading] = useState(false);
    const [isDepartLoading, setIsDepartLoading] = useState(false);
    const [isSkipLoading, setIsSkipLoading] = useState(false);
    const [isCorrectLoading, setIsCorrectLoading] = useState(false);

    // Confirmation dialog state
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [pendingAction, setPendingAction] = useState(null); // 'arrive' | 'depart' | 'skip' | 'correct'

    const currentState = getEffectiveWaypointState(stop, waypointState);

    const waypointName = getTranslatedWaypointName(
        stop.waypoint_id,
        stop.waypoint_name || stop.name
    );

    const hasArrived = currentState === 'dwelling' || currentState === 'departing';
    const hasPassed = currentState === 'passed';
    const isLoading = isArriveLoading || isDepartLoading || isSkipLoading || isCorrectLoading;
    // The first and final waypoints anchor the trip plan and cannot be bypassed.
    const canManualSkipThisWaypoint = !isFirstWaypoint && !isLastWaypoint;

    // Open confirmation dialog for an action
    const requestAction = (action) => async (e) => {
        e.stopPropagation();
        if (isLoading) return;

        // Active correction uses the dedicated co-located selection flow.
        // Pre-trip manual-skip correction has no arrival selection, so it uses
        // this component's existing generic confirmation shell.
        if (action === 'correct' && onManualCorrection && !confirmCorrectionInline) {
            try {
                setIsCorrectLoading(true);
                await onManualCorrection(stop.waypoint_id, waypointName);
            } finally {
                setIsCorrectLoading(false);
            }
            return;
        }

        setPendingAction(action);
        setShowConfirmDialog(true);
    };

    // Execute the confirmed action
    const executeAction = async () => {
        if (!pendingAction) return;

        try {
            if (pendingAction === 'arrive' && onManualArrival) {
                setIsArriveLoading(true);
                await onManualArrival(stop.waypoint_id, waypointName);
            } else if (pendingAction === 'depart' && onManualDeparture) {
                setIsDepartLoading(true);
                await onManualDeparture(stop.waypoint_id, waypointName);
            } else if (pendingAction === 'skip' && onManualSkip) {
                setIsSkipLoading(true);
                await onManualSkip(stop.waypoint_id, waypointName);
            } else if (pendingAction === 'correct' && onManualCorrection) {
                setIsCorrectLoading(true);
                await onManualCorrection(stop.waypoint_id, waypointName);
            }
        } finally {
            setIsArriveLoading(false);
            setIsDepartLoading(false);
            setIsSkipLoading(false);
            setIsCorrectLoading(false);
            setShowConfirmDialog(false);
            setPendingAction(null);
        }
    };

    // Close dialog without action
    const cancelAction = () => {
        setShowConfirmDialog(false);
        setPendingAction(null);
    };

    // Configuration for confirmation dialog based on action type
    const getDialogConfig = () => {
        switch (pendingAction) {
            case 'arrive':
                return {
                    title: t('confirmArrival'),
                    message: t('confirmArrivalMessage', { waypointName }),
                    description: t('confirmArrivalDescription'),
                    confirmLabel: t('arrive'),
                    confirmVariant: 'success',
                    confirmIcon: ArrowDownToLine,
                    iconVariant: 'success'
                };
            case 'depart':
                return {
                    title: t('confirmDeparture'),
                    message: t('confirmDepartureMessage', { waypointName }),
                    description: t('confirmDepartureDescription'),
                    confirmLabel: t('depart'),
                    confirmVariant: 'warning',
                    confirmIcon: ArrowRightFromLine,
                    iconVariant: 'warning'
                };
            case 'correct':
                return {
                    title: t('confirmCorrection'),
                    message: t(
                        preTripCommunication
                            ? 'confirmPreTripCorrectionMessage'
                            : 'confirmCorrectionMessage',
                        { waypointName }
                    ),
                    description: t(
                        preTripCommunication
                            ? 'confirmPreTripCorrectionDescription'
                            : 'confirmCorrectionDescription'
                    ),
                    confirmLabel: t('correct'),
                    confirmVariant: 'danger',
                    confirmIcon: RotateCcw,
                    iconVariant: 'danger'
                };
            case 'skip':
                return {
                    title: t('confirmSkip'),
                    message: t(
                        preTripCommunication
                            ? 'confirmPreTripSkipMessage'
                            : 'confirmSkipMessage',
                        { waypointName }
                    ),
                    description: t(
                        preTripCommunication
                            ? 'confirmPreTripSkipDescription'
                            : 'confirmSkipDescription'
                    ),
                    confirmLabel: t('skip'),
                    confirmVariant: 'warning',
                    confirmIcon: SkipForward,
                    iconVariant: 'warning'
                };
            default:
                return {};
        }
    };

    const dialogConfig = getDialogConfig();

    // Render confirmation dialog
    const renderConfirmDialog = () => (
        <ConfirmDialogShell
            isOpen={showConfirmDialog}
            onClose={cancelAction}
            onConfirm={executeAction}
            title={dialogConfig.title}
            size="sm"
            confirmLabel={dialogConfig.confirmLabel}
            confirmVariant={dialogConfig.confirmVariant}
            confirmIcon={dialogConfig.confirmIcon}
            iconVariant={dialogConfig.iconVariant}
            showIcon={true}
            isLoading={isLoading}
        >
            <div className="text-center space-y-3">
                <p className={cn("text-base font-medium")}>
                    {dialogConfig.message}
                </p>
                <p className={cn("text-sm text-gray-500 dark:text-gray-400")}>
                    {dialogConfig.description}
                </p>
            </div>
        </ConfirmDialogShell>
    );

    // For passed waypoints, show only the "Correct" button
    if (hasPassed) {
        if (!onManualCorrection) return null;

        return (
            <>
                <div className="flex items-center gap-2 mt-2">
                    <button
                        onClick={requestAction('correct')}
                        disabled={isLoading}
                        className={cn(
                            "inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded transition-colors",
                            "bg-red-100 dark:bg-red-900/30",
                            "text-red-700 dark:text-red-300",
                            "hover:bg-red-200 dark:hover:bg-red-900/50",
                            "disabled:opacity-50 disabled:cursor-not-allowed"
                        )}
                        title={t('correctTooltip')}
                    >
                        <RotateCcw className="h-3.5 w-3.5" />
                        {isCorrectLoading ? t('loading') : t('correct')}
                    </button>
                </div>
                {renderConfirmDialog()}
            </>
        );
    }

    return (
        <>
            <div className="flex items-center gap-2 mt-2">
                {onManualSkip && canManualSkipThisWaypoint && (
                    <button
                        onClick={requestAction('skip')}
                        disabled={isLoading}
                        className={cn(
                            "inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded transition-colors",
                            "bg-yellow-100 dark:bg-yellow-900/30",
                            "text-yellow-700 dark:text-yellow-300",
                            "hover:bg-yellow-200 dark:hover:bg-yellow-900/50",
                            "disabled:opacity-50 disabled:cursor-not-allowed"
                        )}
                        title={t('skipTooltip')}
                    >
                        <SkipForward className="h-3.5 w-3.5" />
                        {isSkipLoading ? t('loading') : t('skip')}
                    </button>
                )}

                {/* Show "Arrive" button for pending/approaching waypoints (not yet arrived) */}
                {!hasArrived && onManualArrival && (
                    <button
                        onClick={requestAction('arrive')}
                        disabled={isLoading}
                        className={cn(
                            "inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded transition-colors",
                            "bg-green-100 dark:bg-green-900/30",
                            "text-green-700 dark:text-green-300",
                            "hover:bg-green-200 dark:hover:bg-green-900/50",
                            "disabled:opacity-50 disabled:cursor-not-allowed"
                        )}
                        title={t('arriveTooltip')}
                    >
                        <ArrowDownToLine className="h-3.5 w-3.5" />
                        {isArriveLoading ? t('loading') : t('arrive')}
                    </button>
                )}

                {/* Show "Depart" button for all waypoints except the last one (will auto-create arrival if needed) */}
                {onManualDeparture && !isLastWaypoint && (
                    <button
                        onClick={requestAction('depart')}
                        disabled={isLoading}
                        className={cn(
                            "inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded transition-colors",
                            "bg-amber-100 dark:bg-amber-900/30",
                            "text-amber-700 dark:text-amber-300",
                            "hover:bg-amber-200 dark:hover:bg-amber-900/50",
                            "disabled:opacity-50 disabled:cursor-not-allowed"
                        )}
                        title={t('departTooltip')}
                    >
                        <ArrowRightFromLine className="h-3.5 w-3.5" />
                        {isDepartLoading ? t('loading') : t('depart')}
                    </button>
                )}
            </div>
            {renderConfirmDialog()}
        </>
    );
};

const LiveTripView = ({
    displayData,
    relevantStops,
    isDriver,
    showWaypoints,
    setShowWaypoints,
    pickupStopId,
    dropoffStopId,
    tripData,
    getTranslatedWaypointName,
    handleOpenBoardingInfo,
    waypointStates = {},
    canManualOverride = false,
    canManualSkip = false,
    onManualArrival = null,
    onManualDeparture = null,
    onManualSkip = null,
    onManualCorrection = null,
    onPreTripManualCorrection = null,
    connectionStatus = null,
    entityTimeZone = null,
    operationalDelayCarriers = new Map()
}) => {
    const { t } = useTranslation();
    const { formatTime, formatDateTime, distanceUnit } = useDateTimePreferences();
    const { text, background, border, components, shadow, cn } = useTheme();

    // Check if WebSocket connection is stale or reconnecting
    const isConnectionStale = connectionStatus === 'stale_reconnecting' ||
        connectionStatus === 'reconnecting' ||
        connectionStatus === 'disconnected' ||
        connectionStatus === 'error' ||
        connectionStatus === 'access_denied';
    const isDistanceOnlyEtaView = displayData?.type === 'distance_only'
        || displayData?.etaDisabled === true
        || displayData?.source === 'none';
    const isActiveUnavailable = displayData?.type === 'active_unavailable';

    const getStatusTitle = () => {
        if (isActiveUnavailable) return t('etaUnavailable');
        // Use simple "ETA" when we have a badge to indicate live/stale status
        if (displayData?.isLive || displayData?.type === 'websocket_realtime') {
            return t('ETA');
        }
        if (displayData?.type === 'estimated') return t('Planned ETA');
        return t('Trip ETA');
    };

    const getDataSourceBadge = () => {
        if (displayData?.isLive || displayData?.type === 'websocket_realtime') {
            // Show "Stale" badge with warning color if connection is not healthy
            // or the rows are a frozen last-known snapshot (location feed lost).
            if (isConnectionStale || displayData?.isStalePresentation === true) {
                return <span className={cn("text-xs px-2 py-1 rounded-full", components.badge.warning)}>{t('Stale')}</span>;
            }
            return <span className={cn("text-xs px-2 py-1 rounded-full", components.badge.success)}>{t('Live')}</span>;
        }
        if (displayData?.type === 'estimated') {
            return <span className={cn("text-xs px-2 py-1 rounded-full", components.badge.primary)}>{t('estimated')}</span>;
        }
        return null;
    };

    return (
        <div className={cn(
            "rounded-lg p-4",
            components.card.base,
            shadow.DEFAULT
        )}>
            <div className="flex justify-between items-center mb-4">
                <h3 className={cn(
                    "text-lg font-semibold flex items-center gap-2",
                    text.primary
                )}>
                    {isDistanceOnlyEtaView
                        ? <MapPin className="h-5 w-5" />
                        : <Clock className="h-5 w-5" />}
                    {isDistanceOnlyEtaView ? t('segmentDistance') : getStatusTitle()}
                    {getDataSourceBadge()}
                </h3>

                {displayData?.source === 'google_maps_api' && (
                    <span className={cn("text-xs flex items-center", text.muted)}>
                        {t('powered by')}
                        <span className="ml-1">
                            <span style={{ color: '#4285F4' }}>G</span>
                            <span style={{ color: '#EA4335' }}>o</span>
                            <span style={{ color: '#FBBC05' }}>o</span>
                            <span style={{ color: '#4285F4' }}>g</span>
                            <span style={{ color: '#34A853' }}>l</span>
                            <span style={{ color: '#EA4335' }}>e</span>
                        </span>
                    </span>
                )}

                {isDriver && displayData.waypoints && displayData.waypoints.length > 1 && (
                    <button
                        onClick={() => {
                            console.log(`[LiveTripView] Toggle waypoints from ${showWaypoints} to ${!showWaypoints}`);
                            setShowWaypoints(!showWaypoints);
                        }}
                        className={cn(
                            "text-sm focus:outline-none",
                            text.link,
                            text.linkHover
                        )}
                    >
                        {showWaypoints ? t('Hide Route Details') : t('Show Route Details')}
                    </button>
                )}
            </div>

            <div className="space-y-3">
                {relevantStops.map((stop, index) => {
                    const isUserPickup = (!isDriver && stop.waypoint_id === pickupStopId) || stop.is_user_pickup;
                    const isUserDropoff = (!isDriver && stop.waypoint_id === dropoffStopId) || stop.is_user_dropoff;
                    const isUserPrimaryStop = isUserPickup || isUserDropoff;
                    const stopIsPickup = stop.can_pickup === 1 || stop.can_pickup === true;
                    const stopIsDropoff = stop.can_dropoff === 1 || stop.can_dropoff === true;
                    const isDepartureLocation = stop.is_departure === true || stop.point_type === 'start';
                    const isStartRow = stop.is_start_row === true || isDepartureLocation;

                    const waypointState = waypointStates[stop.waypoint_id];
                    const previousVisibleStop = index > 0 ? relevantStops[index - 1] : null;
                    const etaStopDisplayState = getEtaStopDisplayState({
                        stop,
                        waypointState,
                        previousVisibleStop,
                        previousWaypointState: previousVisibleStop ? waypointStates[previousVisibleStop.waypoint_id] : null,
                        displayData,
                        tripStatus: tripData?.status,
                        index,
                        totalStops: relevantStops.length
                    });
                    const {
                        isPassed,
                        isSkipped,
                        isWebsocketRealtime,
                        isLiveEtaView,
                        isDeparting,
                        isDwelling,
                        suppressLiveProgressDisplay,
                        hasLiveEtaTimestamp,
                        displayEtaMinutes
                    } = etaStopDisplayState;
                    // A fallback clock synthesized by the client is not proof
                    // that Operational Delay was included. This authority is
                    // used only for next-visible pending wording; declaration
                    // history itself comes from the backend crossed boolean.
                    const isDelayApplicableLifecycle = [
                        'scheduled',
                        'dispatched',
                        'in_progress'
                    ].includes(tripData?.status);
                    const hasEffectiveEtaAuthority = isDelayApplicableLifecycle
                        && !isDistanceOnlyEtaView
                        && (
                            finiteNumberOrNull(stop.eta_minutes_with_operational_delay) !== null
                            || (
                                !displayData?.isLive
                                && Number.isFinite(Date.parse(
                                    stop.boundary_time_with_operational_delay
                                ))
                            )
                        );
                    const carrierDeclarations = operationalDelayCarriers.get(
                        Number(stop.waypoint_id)
                    ) || [];
                    const localDeclarations = carrierDeclarations.filter(({ placement }) => (
                        placement === 'local'
                    ));
                    const fallbackDeclarations = carrierDeclarations.filter(({ placement }) => (
                        placement === 'fallback'
                    ));
                    const fallbackHistoricalMinutes = fallbackDeclarations
                        .filter(({ boundary_crossed: crossed }) => crossed)
                        .reduce((sum, declaration) => sum + declaration.delay_minutes, 0);
                    const fallbackPendingMinutes = fallbackDeclarations
                        .filter(({ boundary_crossed: crossed }) => !crossed)
                        .reduce((sum, declaration) => sum + declaration.delay_minutes, 0);
                    const operationalDelayLabels = [];
                    if (isDelayApplicableLifecycle && !isDistanceOnlyEtaView && !isSkipped) {
                        localDeclarations.forEach((declaration) => {
                            const boundary = declaration.boundary_kind === 'arrival'
                                ? 'arrival'
                                : 'departure';
                            const tense = declaration.boundary_crossed
                                ? 'Historical'
                                : 'Pending';
                            operationalDelayLabels.push({
                                key: `${boundary}-${tense}`,
                                testId: `operational-delay-${boundary}-${stop.waypoint_id}`,
                                text: t(`operationalDelay.${boundary}${tense}`, {
                                    count: declaration.delay_minutes
                                }),
                                included: false
                            });
                        });
                        if (fallbackHistoricalMinutes > 0) {
                            operationalDelayLabels.push({
                                key: 'fallback-historical',
                                testId: `operational-delay-fallback-historical-${stop.waypoint_id}`,
                                text: t('operationalDelay.genericHistorical', {
                                    count: fallbackHistoricalMinutes
                                }),
                                included: false
                            });
                        }
                        if (fallbackPendingMinutes > 0) {
                            const canClaimIncluded = !isPassed && hasEffectiveEtaAuthority;
                            operationalDelayLabels.push({
                                key: 'fallback-pending',
                                testId: canClaimIncluded
                                    ? `operational-delay-included-${stop.waypoint_id}`
                                    : `operational-delay-fallback-pending-${stop.waypoint_id}`,
                                text: canClaimIncluded
                                    ? t('operationalDelay.includedBadge', {
                                        minutes: fallbackPendingMinutes
                                    })
                                    : t('operationalDelay.genericPending', {
                                        count: fallbackPendingMinutes
                                    }),
                                included: canClaimIncluded
                            });
                        }
                    }
                    const primaryStopAccent = isUserPickup
                        ? "ring-2 ring-sky-300/80 border-sky-300 bg-sky-50 dark:bg-sky-950/30 dark:border-sky-700"
                        : isUserDropoff
                            ? "ring-2 ring-rose-300/80 border-rose-300 bg-rose-50 dark:bg-rose-950/30 dark:border-rose-700"
                            : null;

                    // Use different icon for skipped/passed waypoints
                    const icon = isSkipped
                        ? <SkipForward className="h-5 w-5 mr-2 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                        : isPassed
                            ? <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0 text-green-500 dark:text-green-400" />
                            : <MapPin className={cn(
                                "h-5 w-5 mr-2 flex-shrink-0",
                                isUserPickup && "text-sky-600 dark:text-sky-300",
                                isUserDropoff && "text-rose-600 dark:text-rose-300"
                            )} />;

                    // Muted styling for passed waypoints
                    const cardClasses = cn(
                        "p-3 rounded-lg border-l-4 transition-all duration-200",
                        isPassed
                            ? "opacity-60 bg-gray-50 dark:bg-gray-800/50 border-gray-300 dark:border-gray-600"
                            : isUserPrimaryStop
                                ? `${background.secondary} ${border.default} ${shadow.sm} ${primaryStopAccent || ''}`
                                : `${background.secondary} ${border.default}`
                    );

                    // In live mode, only show backend remaining-distance-from-current.
                    // Falling back to segment distance can be misleading for newly reactivated stops.
                    const distanceValue = isLiveEtaView
                        ? stop.distance_meters_from_current
                        : (stop.distance_meters_from_current ?? stop.dynamic_distance_meters);
                    const normalizedDistanceValue = finiteNumberOrNull(distanceValue);
                    const hasRenderablePositiveDistance = normalizedDistanceValue !== null
                        && normalizedDistanceValue > 0
                        && !suppressLiveProgressDisplay;
                    const showFallbackEstimateWarning = isDriver
                        && isLiveEtaView
                        && !isDistanceOnlyEtaView
                        && !isSkipped
                        && !isPassed
                        && !suppressLiveProgressDisplay
                        && isFallbackEstimate(stop);
                    const showDistanceOnlyApproxBadge = isDriver
                        && isLiveEtaView
                        && isDistanceOnlyEtaView
                        && !isSkipped
                        && !isPassed
                        && hasRenderablePositiveDistance
                        && isFallbackEstimate(stop);

                    const pickupRiders = stop.pickup_riders || 0;
                    const dropoffRiders = stop.dropoff_riders || 0;
                    const pickupLuggages = stop.pickup_luggages || 0;
                    const dropoffLuggages = stop.dropoff_luggages || 0;
                    const showNeutralNoBookingLabel = !isDriver
                        && !isSkipped
                        && !isPassed
                        && !isDepartureLocation
                        && !isUserPrimaryStop
                        && !stopIsPickup
                        && !stopIsDropoff;
                    const canShowBoardingInfo = stop.point_type === 'stop'
                        && hasBoardingInfo(stop)
                        && (isDriver || isUserPrimaryStop);

                    // Determine if this is a pre-trip view (scheduled/dispatched)
                    const isPreTrip = tripData?.status === 'scheduled' || tripData?.status === 'dispatched';
                    const showRightColumn = !isDistanceOnlyEtaView
                        || isSkipped
                        || isPassed
                        || isDeparting
                        || isDwelling
                        || isDepartureLocation
                        || stop.stay_duration_minutes > 0;

                    return (
                        <div
                            key={stop.waypoint_id || index}
                            data-testid={`eta-stop-card-${stop.waypoint_id || index}`}
                            className={isSkipped ? cn(
                                "p-3 rounded-lg border-l-4 transition-all duration-200",
                                "opacity-50 bg-gray-50 dark:bg-gray-800/30 border-dashed border-gray-300 dark:border-gray-600"
                            ) : cardClasses}
                        >
                            <div className="flex justify-between items-start gap-4">
                                <div className="flex items-start flex-grow min-w-0">
                                    {icon}
                                    <div className="flex-grow min-w-0">
                                        <div className={cn(
                                            "flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1",
                                            text.primary
                                        )}>
                                            <span className="min-w-0 max-w-full truncate font-medium">
                                                {getTranslatedWaypointName(stop.waypoint_id, stop.waypoint_name || stop.name || `${t('Stop')} ${stop.sequence_number}`)}
                                            </span>
                                            {isDepartureLocation && (
                                                <span className={cn("text-xs font-normal", text.muted)}>
                                                    ({t('startLocation')})
                                                </span>
                                            )}
                                            {!isDepartureLocation && isUserPickup && (
                                                <span className="inline-flex flex-shrink-0 items-center rounded-full bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-700 dark:bg-sky-900/40 dark:text-sky-200">
                                                    {t('Your Pickup')}
                                                </span>
                                            )}
                                            {!isDepartureLocation && isUserDropoff && (
                                                <span className="inline-flex flex-shrink-0 items-center rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-700 dark:bg-rose-900/40 dark:text-rose-200">
                                                    {t('Your Dropoff')}
                                                </span>
                                            )}
                                            {!isDepartureLocation && isDriver && !stopIsPickup && !stopIsDropoff && (
                                                <span className={cn("text-xs font-normal", text.muted)}> ({t('Transit Stop')})</span>
                                            )}
                                        </div>

                                        {isSkipped ? (
                                            <div className={cn("text-xs mt-0.5 italic", text.disabled)}>
                                                {stop.is_manual_skip ? t('manuallySkipped') : t('noRidersSkipped')}
                                            </div>
                                        ) : showNeutralNoBookingLabel ? (
                                            <div className={cn("text-xs mt-0.5 italic", text.disabled)}>
                                                {t('noRidersSkipped')}
                                            </div>
                                        ) : isPreTrip && !isDepartureLocation && stop.cumulative_distance_from_start_meters > 0 ? (
                                            // Pre-trip: show cumulative distance from trip start.
                                            <div className={cn("text-xs mt-0.5", text.muted)}>
                                                {formatDistance(stop.cumulative_distance_from_start_meters, distanceUnit, t)}
                                            </div>
                                        ) : hasRenderablePositiveDistance && (
                                            <div className={cn("text-xs mt-0.5", text.muted)}>
                                                {formatDistance(normalizedDistanceValue, distanceUnit, t)}
                                                {stop.gross_dynamic_distance_meters > 0 &&
                                                stop.gross_dynamic_distance_meters !== stop.dynamic_distance_meters && (
                                                    <span className={cn("ml-1", text.disabled)}>
                                                        {/*({formatDistance(stop.gross_dynamic_distance_meters, distanceUnit, t)} {t('total')})*/}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        {(showFallbackEstimateWarning || showDistanceOnlyApproxBadge) && (
                                            <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-amber-700 dark:text-amber-300">
                                                <span className="inline-flex items-center gap-1 rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 font-medium text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200">
                                                    <AlertTriangle className="h-3 w-3" />
                                                    {t('etaFallbackApproxBadge')}
                                                </span>
                                                {showFallbackEstimateWarning && (
                                                    <span>{t('etaFallbackStraightLineDetail')}</span>
                                                )}
                                            </div>
                                        )}

                                        {isDriver && (
                                            <div className={cn("text-xs mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1", text.secondary)}>
                                                {stopIsPickup && (pickupRiders > 0 || pickupLuggages > 0) && (
                                                    <div className="flex items-center gap-2">
                                                        <span className={cn("font-medium", text.secondary)}>{t('pickup')}:</span>
                                                        {pickupRiders > 0 && (
                                                            <span className="flex items-center">
                                                                <UserPlus className="h-3.5 w-3.5 mr-1 text-gray-500" />
                                                                {pickupRiders}
                                                            </span>
                                                        )}
                                                        {pickupLuggages > 0 && (
                                                            <span className="flex items-center">
                                                                <Briefcase className="h-3.5 w-3.5 mr-1 text-gray-500" />
                                                                {pickupLuggages}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                                {stopIsDropoff && (dropoffRiders > 0 || dropoffLuggages > 0) && (
                                                    <div className="flex items-center gap-2">
                                                        <span className={cn("font-medium", text.secondary)}>{t('dropoff')}:</span>
                                                        {dropoffRiders > 0 && (
                                                            <span className="flex items-center">
                                                                <UserPlus className="h-3.5 w-3.5 mr-1 text-gray-500" />
                                                                {dropoffRiders}
                                                            </span>
                                                        )}
                                                        {dropoffLuggages > 0 && (
                                                            <span className="flex items-center">
                                                                <Briefcase className="h-3.5 w-3.5 mr-1 text-gray-500" />
                                                                {dropoffLuggages}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Boarding Info Badge */}
                                        {canShowBoardingInfo && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleOpenBoardingInfo(stop);
                                                }}
                                                className={cn(
                                                    "inline-flex items-center gap-1 mt-1 px-2 py-0.5 text-xs font-medium rounded transition-colors",
                                                    "bg-blue-100 dark:bg-blue-900/30",
                                                    "text-blue-700 dark:text-blue-300",
                                                    "hover:bg-blue-200 dark:hover:bg-blue-900/50"
                                                )}
                                                title={t('boardingInfoAvailable')}
                                            >
                                                <MapPin className="h-3 w-3" />
                                                {t('findLocation')}
                                            </button>
                                        )}

                                        {/* Active lifecycle controls stay in-progress only. */}
                                        {isDriver && (!isSkipped || isPassed) && canManualOverride && tripData?.status === 'in_progress' && (
                                            <ManualOverrideButtons
                                                stop={stop}
                                                waypointState={waypointStates[stop.waypoint_id]}
                                                onManualArrival={onManualArrival}
                                                onManualDeparture={onManualDeparture}
                                                onManualSkip={onManualSkip}
                                                onManualCorrection={onManualCorrection}
                                                getTranslatedWaypointName={getTranslatedWaypointName}
                                                isFirstWaypoint={index === 0}
                                                isLastWaypoint={index === relevantStops.length - 1}
                                                t={t}
                                                cn={cn}
                                            />
                                        )}
                                        {/* Pre-trip controls communicate plan/ETA intent only. */}
                                        {isPreTrip
                                            && canManualSkip
                                            && index > 0
                                            && index < relevantStops.length - 1
                                            && ((!isSkipped && !isPassed) || stop.is_manual_skip === true)
                                            && (
                                                <ManualOverrideButtons
                                                    stop={stop}
                                                    waypointState={waypointStates[stop.waypoint_id]}
                                                    onManualArrival={null}
                                                    onManualDeparture={null}
                                                    onManualSkip={!isSkipped ? onManualSkip : null}
                                                    onManualCorrection={
                                                        stop.is_manual_skip === true
                                                            ? onPreTripManualCorrection
                                                            : null
                                                    }
                                                    getTranslatedWaypointName={getTranslatedWaypointName}
                                                    isFirstWaypoint={index === 0}
                                                    isLastWaypoint={index === relevantStops.length - 1}
                                                    preTripCommunication
                                                    confirmCorrectionInline
                                                    t={t}
                                                    cn={cn}
                                                />
                                            )}
                                    </div>
                                </div>

                                {showRightColumn && (
                                  <div className={cn("text-right flex-shrink-0", isActiveUnavailable && "max-w-[45%]")}>
                                    <div className={cn(
                                        "font-semibold",
                                        isSkipped
                                            ? "text-base text-gray-400 dark:text-gray-500 italic"
                                            : isPassed
                                                ? "text-base text-gray-500 dark:text-gray-400"
                                                : isUserPrimaryStop
                                                    ? 'text-lg'
                                                    : `text-base ${text.primary}`
                                    )}>
                                        {isDistanceOnlyEtaView
                                            ? isSkipped
                                                ? '—'
                                                : isPassed
                                                    ? (index === relevantStops.length - 1 ? t('Arrived') : t('Departed'))
                                                    : isDeparting
                                                        ? t('waypointStates.departing')
                                                        : isDwelling
                                                            ? t('Arrived')
                                                            : isDepartureLocation
                                                                ? t('Departure')
                                                                : null
                                            : isSkipped
                                                ? '—'
                                                : isPassed
                                                    ? (index === relevantStops.length - 1 ? t('Arrived') : t('Departed'))
                                                    : isDeparting
                                                        ? t('waypointStates.departing')
                                                        : isDwelling
                                                            ? t('Arrived')
                                                            : isActiveUnavailable
                                                                ? t('etaUnavailable')
                                                                : isPreTrip && isStartRow
                                                                    ? (formatTimeLocal(stop.estimated_arrival_time, formatTime, formatDateTime, t, entityTimeZone) || t('Departure'))
                                                                    : isPreTrip && stop.has_eta_data === false
                                                                        ? t('No ETA yet')
                                                                        : isDepartureLocation
                                                                            ? t('Departure')
                                                                            : (formatTimeLocal(stop.estimated_arrival_time, formatTime, formatDateTime, t, entityTimeZone) || '--:--')}
                                    </div>
                                    {/* Show departure time for passed waypoints */}
                                    {isPassed && stop.waypoint_departure_time && (
                                        <div className={cn("text-xs mt-0.5", "text-gray-500 dark:text-gray-400")}>
                                            {formatTimeLocal(stop.waypoint_departure_time, formatTime, formatDateTime, t, entityTimeZone)}
                                        </div>
                                    )}
                                    {/* Pending correction overrides retained arrival, including
                                        before the cleared trip-stop row reaches this render. */}
                                    {isActiveUnavailable && ['dwelling', 'departing'].includes(etaStopDisplayState.effectiveWaypointState)
                                        && stop.waypoint_arrival_time && (
                                        <div className={cn("text-xs mt-0.5", "text-gray-500 dark:text-gray-400")}>
                                            {formatTimeLocal(stop.waypoint_arrival_time, formatTime, formatDateTime, t, entityTimeZone)}
                                        </div>
                                    )}
                                    {/* Show ETA minutes only for in_progress trips with real-time data (not for passed/skipped waypoints) */}
                                    {/* ETA-based display: Arrived (dwelling) > Arriving (ETA <= 1 min) > X min > -- */}
                                    {!isDistanceOnlyEtaView && !isSkipped && !isPassed && !suppressLiveProgressDisplay && tripData?.status === 'in_progress' && (displayData?.isLive || isWebsocketRealtime) && (
                                        <div className={cn(
                                            "text-xs mt-0.5",
                                            text.muted
                                        )}>
                                            {isDwelling
                                                ? t('Arrived')
                                                : hasLiveEtaTimestamp && displayEtaMinutes !== null && displayEtaMinutes <= ETA_ARRIVING_THRESHOLD_MINUTES
                                                    ? t('Arriving')
                                                    : hasLiveEtaTimestamp && displayEtaMinutes > 0
                                                        ? `${displayEtaMinutes} ${t('min')}`
                                                        : '--'
                                            }
                                        </div>
                                    )}
                                    {/* Pre-trip: show cumulative travel duration from start (consistent with cumulative distance and arrival time) */}
                                    {!isDistanceOnlyEtaView && !isSkipped && isPreTrip && !isDepartureLocation && stop.cumulative_duration_from_start_seconds > 0 && (
                                        <div className={cn("text-xs mt-0.5", text.muted)}>
                                            {`${t('Travel')}: ${formatDuration(stop.cumulative_duration_from_start_seconds / 60, t)}`}
                                        </div>
                                    )}
                                    {/* In-progress/other: show segment travel time (not for skipped waypoints) */}
                                    {!isDistanceOnlyEtaView && !isSkipped && !isPreTrip && !displayData?.isLive && (stop.dynamic_duration_minutes > 0 || stop.effective_stay_duration > 0) && (
                                        <div className={cn("text-xs mt-0.5", text.muted)}>
                                            {stop.dynamic_duration_minutes > 0 && `${t('Travel')}: ${formatDuration(stop.dynamic_duration_minutes, t)}`}
                                            {stop.gross_dynamic_duration_minutes > 0 &&
                                            stop.gross_dynamic_duration_minutes !== stop.dynamic_duration_minutes && (
                                                <span className={cn("ml-1", text.disabled)}>
                                                    ({formatDuration(stop.gross_dynamic_duration_minutes, t)} {t('total')})
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    {/* Show stay time for stops that have actual pickup or dropoff activity (not for skipped) */}
                                    {!isSkipped && (stop.stay_duration_minutes > 0) && (
                                        <div className={cn("text-xs mt-0.5", text.muted)}>
                                            {`${t('Stay')}: ${formatDuration(stop.stay_duration_minutes, t)}`}
                                        </div>
                                    )}
                                  </div>
                                )}
                            </div>
                            {operationalDelayLabels.length > 0 && (
                                <div className="mt-1 flex flex-col items-end gap-1 text-right">
                                    {operationalDelayLabels.map((label) => (
                                        <div
                                            key={label.key}
                                            data-testid={label.testId}
                                            className={label.included
                                                ? "inline-flex max-w-full rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
                                                : "block max-w-full text-xs font-medium text-amber-700 dark:text-amber-300"}
                                        >
                                            {label.text}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* {displayData?.type === 'websocket_realtime' && displayData?.duration_summary && (
                <div className={cn("mt-4 p-3 rounded-lg text-sm", background.secondary)}>
                    <div className={cn("flex justify-between", text.secondary)}>
                        <span>{t('Total Time')}:</span>
                        <span className={cn("font-medium", text.primary)}>
                            {displayData.duration_summary.traffic_duration_minutes} {t('min')}
                        </span>
                    </div>

                    {displayData.duration_summary.traffic_delay_minutes > 0 && (
                        <div className="flex justify-between text-amber-600 dark:text-amber-400 mt-1">
                            <span>{t('Traffic Delay')}:</span>
                            <span className="font-medium">
                                +{displayData.duration_summary.traffic_delay_minutes} {t('min')}
                            </span>
                        </div>
                    )}
                </div>
            )} */}
        </div>
    );
};

export default LiveTripView;
